if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        const connection = {};
        this.initializationConnection = connection;
        const form = this.productForm;
        // A child tag can exist before its class is defined, or while its
        // upgrade reaction is still queued during section insertion.
        if (form && !form.submitButton) {
          this.initializationInertState = this.hasAttribute('inert');
          this.setAttribute('inert', '');
          customElements.whenDefined('product-form').then(() => {
            if (!this.isConnected || this.initializationConnection !== connection) return;
            customElements.upgrade(form);
            this.initializeProductInfo();
          });
          return;
        }
        this.initializeProductInfo();
      }

      initializeProductInfo() {
        if (!this.productSwapInitialized) {
          this.initializeProductSwapUtility();
          this.productSwapInitialized = true;
        }
        this.committedVariantSelectors = this.variantSelectors?.cloneNode(true);

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.restoreInitializationInert();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      restoreInitializationInert() {
        if (this.initializationInertState === undefined) return;
        this.toggleAttribute('inert', this.initializationInertState);
        this.initializationInertState = undefined;
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        // Reconnection must not mistake our quantity-limit lock for a server
        // sold-out state. Variant responses update this snapshot explicitly.
        if (this.variantSubmitDisabled === undefined) {
          this.variantSubmitDisabled = !!this.productForm?.submitButton.disabled;
        }
        this.setQuantityBoundries();
        if (!this.dataset.originalSection && !this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.initializationConnection = undefined;
        this.restoreInitializationInert();
        this.onVariantChangeUnsubscriber?.();
        this.onVariantChangeUnsubscriber = undefined;
        this.abortController?.abort();
        this.quantityAbortController?.abort();
        this.activeSelectionDeferred?.reject(new DOMException('Product removed', 'AbortError'));
        this.activeSelectionDeferred = null;
        this.cartUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber = undefined;
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        const selectors = this.variantSelectors;
        const focusTargetId = event.target.id || target.id;
        const deferred = selectors?.takePendingSelectPromise();
        this.activeSelectionDeferred?.reject(new DOMException('Selection superseded', 'AbortError'));
        this.activeSelectionDeferred = deferred;
        if (this.dataset.variantPending !== 'true') {
          this.variantRollback = {
            selectors: this.committedVariantSelectors,
            disabled: this.productForm?.submitButton.disabled,
            label: this.productForm?.submitButtonText.textContent,
            idDisabled: this.productForm?.variantIdInput.disabled,
            inquiryInert: this.querySelector('[data-product-inquiry]')?.hasAttribute('inert'),
            paymentInert: Array.from(this.querySelectorAll('.shopify-payment-button'), (button) => button.hasAttribute('inert')),
          };
        }
        this.quantityAbortController?.abort();
        this.dataset.variantPending = 'true';
        this.resetProductFormState();
        if (this.productForm) this.productForm.variantIdInput.disabled = true;
        this.querySelectorAll('.shopify-payment-button').forEach((button) => button.setAttribute('inert', ''));

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;
        let committing = false;
        return this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: focusTargetId,
          callback: (html) => {
            const variant = this.validateVariantResponse(html, shouldFetchFullPage);
            committing = true;
            const update = shouldSwapProduct
              ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
              : this.handleUpdateProductInfo(productUrl);
            update(html);
            if (!shouldSwapProduct) {
              this.dataset.variantPending = 'false';
              if (this.productForm) this.productForm.variantIdInput.disabled = !variant?.available;
              // Payment observers must see the committed, enabled form ID.
              this.updateVariantInputs(variant?.id);
              this.querySelectorAll('.shopify-payment-button').forEach((button) => button.toggleAttribute('inert', !variant?.available || this.quantityInput?.dataset.quantityUnavailable === 'true'));
              this.committedVariantSelectors = this.variantSelectors?.cloneNode(true);
            }
            // The promise belongs to this request, never whichever change happened last.
            if (selectors && deferred) {
              selectors.pendingSelectPromise = deferred;
              selectors.resolvePendingSelectPromise(variant, this.getVariantSelects(html));
            }
            this.activeSelectionDeferred = null;
            this.variantRollback = null;
          },
          onError: (error) => {
            deferred?.reject(error);
            this.activeSelectionDeferred = null;
            const rollback = this.variantRollback;
            if (!committing && rollback) {
              if (rollback.selectors) this.variantSelectors?.replaceWith(rollback.selectors.cloneNode(true));
              const form = this.productForm;
              form?.toggleSubmitButton(rollback.disabled, rollback.label);
              if (form) form.variantIdInput.disabled = rollback.idDisabled;
              this.querySelector('[data-product-inquiry]')?.toggleAttribute('inert', !!rollback.inquiryInert);
              this.querySelectorAll('.shopify-payment-button').forEach((button, index) => button.toggleAttribute('inert', !!rollback.paymentInert[index]));
              this.dataset.variantPending = 'false';
            } else {
              // A rendering exception after mutation must not submit partially updated state.
              this.productForm?.toggleSubmitButton(true);
              if (this.productForm) this.productForm.variantIdInput.disabled = true;
            }
            this.productForm?.handleErrorMessage(this.dataset.variantError || 'Unable to update this option. Please select it again.');
            this.variantRollback = null;
            document.getElementById(focusTargetId)?.focus({ preventScroll: true });
          },
        });
      }

      validateVariantResponse(html, fullPage) {
        const source = html.querySelector(fullPage ? "product-info[id^='MainProduct']" : 'product-info');
        const json = source?.querySelector('variant-selects [data-selected-variant]');
        if (!json || (fullPage && (!html.querySelector('main') || !html.querySelector('head title')))) {
          throw new Error('Incomplete product response');
        }
        const variant = JSON.parse(json.textContent);
        if (variant !== null) {
          const sectionId = source.dataset.section || this.sectionId;
          const input = html.getElementById(`product-form-${sectionId}`)?.querySelector('[name="id"]');
          if (!variant?.id || (this.productForm && (String(variant.id) !== input?.value ||
              !html.getElementById(`ProductSubmitButton-${sectionId}`))) ||
              (this.querySelector(`#price-${this.dataset.section}`) && !html.getElementById(`price-${sectionId}`))) {
            throw new Error('Inconsistent product response');
          }
        }
        if (variant && this.quantityInput && !fullPage) {
          this.validateQuantityForm(html.getElementById(`Quantity-Form-${this.sectionId}`));
        }
        return variant;
      }

      resetProductFormState() {
        this.querySelector('[data-product-inquiry]')?.setAttribute('inert', '');
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback, onError }) {
        this.abortController?.abort();
        const controller = new AbortController();
        this.abortController = controller;
        const isCurrent = () => this.abortController === controller && !controller.signal.aborted;

        return fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            if (response.ok === false) throw new Error(`Product request failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            if (!isCurrent()) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
            // set focus to last clicked option value without scrolling the gallery
            document.querySelector(`#${targetId}`)?.focus({ preventScroll: true });
          })
          .catch((error) => {
            if (!isCurrent()) return;
            this.pendingRequestUrl = null;
            if (onError) onError(error);
            else this.variantSelectors?.rejectPendingSelectPromise(error);
          });
      }

      parseJsonScript(parent, selector) {
        try {
          return JSON.parse(parent?.querySelector(selector)?.textContent);
        } catch {
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        return this.parseJsonScript(this.getVariantSelects(productInfoNode), '[data-selected-variant]');
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          const variant = this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          if (this.dataset.yeswoodVariantMediaMode !== 'dialog') {
            this.updateMedia(html, variant?.featured_media?.id);
          }

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
              if (id === 'Sku' || id === 'Inventory') {
                destination.classList.toggle('visibility-hidden', source.classList.contains('visibility-hidden'));
              }
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.variantSubmitDisabled = html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true;
          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            this.variantSubmitDisabled ||
              this.quantityInput?.dataset.quantityUnavailable === 'true',
            this.quantityInput?.dataset.quantityUnavailable === 'true' ? window.variantStrings.unavailable : window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          if (this.dataset.variantPending !== 'true') input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const input = this.quantityInput;
        const cartQuantity = Math.max(0, parseInt(input.dataset.cartQuantity) || 0);
        const step = Math.max(1, parseInt(input.step) || 1);
        const ruleMin = Math.max(1, parseInt(input.dataset.min) || 1);
        const ruleMax = input.dataset.max === undefined ? null : parseInt(input.dataset.max);
        // Keep the existing minimum policy, aligned to a legal increment.
        const min = cartQuantity >= ruleMin ? step : Math.ceil(ruleMin / step) * step;
        const max = ruleMax === null ? null : Math.max(0, Math.floor((ruleMax - cartQuantity) / step) * step);
        const unavailable = max !== null && max < min;
        input.min = unavailable ? 0 : min;
        if (max !== null) input.max = unavailable ? 0 : max;
        else input.removeAttribute('max');
        input.value = unavailable ? 0 : min;
        input.dataset.quantityUnavailable = String(unavailable);
        input.disabled = unavailable;
        if (unavailable) {
          if (!this.quantityLimitActive) this.quantityButtonWasDisabled = this.variantSubmitDisabled ?? !!this.productForm?.submitButton.disabled;
          this.quantityLimitActive = true;
          this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
        } else if (this.quantityLimitActive) {
          if (this.dataset.variantPending !== 'true') this.productForm?.toggleSubmitButton(this.quantityButtonWasDisabled);
          this.quantityLimitActive = false;
        }
        this.querySelectorAll('.shopify-payment-button').forEach((button) =>
          button.toggleAttribute('inert', unavailable || this.dataset.variantPending === 'true' || !!this.productForm?.variantIdInput.disabled)
        );
        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId || this.dataset.variantPending === 'true') return;
        this.quantityAbortController?.abort();
        const controller = new AbortController();
        this.quantityAbortController = controller;
        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, { signal: controller.signal })
          .then((response) => {
            if (response.ok === false) throw new Error(`Quantity request failed (${response.status})`);
            return response.text();
          })
          .then((responseText) => {
            if (controller !== this.quantityAbortController || controller.signal.aborted ||
                this.dataset.variantPending === 'true' || this.productForm?.variantIdInput?.value !== currentVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((error) => {
            if (controller !== this.quantityAbortController || controller.signal.aborted) return;
            this.productForm?.handleErrorMessage(window.cartStrings?.error || 'Unable to refresh quantity. Please try again.');
          })
          .finally(() => {
            if (controller === this.quantityAbortController) spinner?.classList.add('hidden');
          });
      }

      validateQuantityForm(form) {
        const input = form?.querySelector('.quantity__input');
        if (!input) throw new Error('Missing quantity rules');
        const validInteger = (attribute, minimum, optional = false) => {
          const value = input.getAttribute(attribute);
          return (optional && value === null) ||
            (value !== null && value.trim() !== '' && Number.isSafeInteger(Number(value)) && Number(value) >= minimum);
        };
        if (!validInteger('data-min', 1) || !validInteger('step', 1) ||
            !validInteger('data-cart-quantity', 0) || !validInteger('data-max', 0, true)) {
          throw new Error('Invalid quantity rules');
        }
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!this.quantityForm) return;
        this.validateQuantityForm(quantityFormUpdated);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
        this.setQuantityBoundries();
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
